import React, { useState, useRef, useEffect } from 'react';
import { SearchIcon, GlobeSearchIcon } from '../common/Icons';
import { api } from '../../services/api-base';

interface LocationSuggestion {
  display_name: string;
  lat: string;
  lon: string;
  place_id: string;
}

// Forward geocoding cache (mirrors revgeo.ts pattern)
const GEOCODE_CACHE = new Map<string, { value: LocationSuggestion[]; ts: number }>();
const GEOCODE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 800;

const normalizeQuery = (q: string) => q.trim().toLowerCase();

const getCachedGeocodeResults = (query: string): LocationSuggestion[] | null => {
  const key = normalizeQuery(query);
  if (!key) return null;

  // Check memory cache first
  const mem = GEOCODE_CACHE.get(key);
  if (mem && Date.now() - mem.ts < GEOCODE_TTL_MS) {
    return mem.value;
  }

  // Check localStorage
  try {
    const raw = localStorage.getItem(`geocode:${key}`);
    if (raw) {
      const obj = JSON.parse(raw);
      if (obj && Array.isArray(obj.results) && obj.ts && Date.now() - obj.ts < GEOCODE_TTL_MS) {
        GEOCODE_CACHE.set(key, { value: obj.results, ts: obj.ts });
        return obj.results;
      }
    }
  } catch {}
  return null;
};

const setCachedGeocodeResults = (query: string, results: LocationSuggestion[]) => {
  const key = normalizeQuery(query);
  if (!key) return;

  const ts = Date.now();
  GEOCODE_CACHE.set(key, { value: results, ts });
  try {
    localStorage.setItem(`geocode:${key}`, JSON.stringify({ results, ts }));
  } catch {}
};

interface LocationSearchProps {
  onLocationFound: (lat: number, lng: number, name: string) => void;
  onExpandedChange?: (expanded: boolean) => void;
}

const LocationSearch: React.FC<LocationSearchProps> = ({ onLocationFound, onExpandedChange }) => {
  const [query, setQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [lastResultQuery, setLastResultQuery] = useState('');
  const [searchError, setSearchError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle click outside to collapse
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
        try { onExpandedChange?.(false); } catch { }
        setQuery('');
        setSuggestions([]);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded, onExpandedChange]);

  // Debounced search with caching and minimum query length
  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setSuggestions([]);
      setSearchError(null);
      setLastResultQuery('');
      return;
    }

    // Require minimum query length to reduce API calls
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setSearchError(null);
      setLastResultQuery('');
      return;
    }

    // Check cache first (instant, no debounce needed)
    const cached = getCachedGeocodeResults(trimmed);
    if (cached) {
      setSuggestions(cached);
      setLastResultQuery(trimmed);
      setSearchError(null);
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      try {
        setSearchError(null);
        setIsLoading(true);
        const res = await api.get('/search/geocode', {
          params: { q: query, limit: 5 },
          signal: controller.signal
        });
        const data = res.data;
        const results = Array.isArray(data) ? data : [];
        setSuggestions(results);
        setLastResultQuery(trimmed);
        // Cache successful results
        setCachedGeocodeResults(trimmed, results);
      } catch (e) {
        if ((e as any)?.name === 'CanceledError') return;
        console.error('Search failed:', e);
        setSuggestions([]);
        setSearchError('Search is temporarily unavailable.');
        setLastResultQuery(trimmed);
      } finally {
        setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [query]);

  const handleIconClick = () => {
    setIsExpanded(true);
    try { onExpandedChange?.(true); } catch { }
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 50);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  // Close on Escape for better UX
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsExpanded(false);
        setQuery('');
        setSuggestions([]);
        try { onExpandedChange?.(false); } catch { }
      }
    };
    if (isExpanded) {
      window.addEventListener('keydown', onKey);
    }
    return () => window.removeEventListener('keydown', onKey);
  }, [isExpanded, onExpandedChange]);

  const chooseResult = (suggestion: LocationSuggestion) => {
    const lat = parseFloat(suggestion.lat);
    const lng = parseFloat(suggestion.lon);

    onLocationFound(lat, lng, suggestion.display_name);
    setSuggestions([]);
    setQuery('');
    setIsExpanded(false);
    try { onExpandedChange?.(false); } catch { }
  };

  if (!isExpanded) {
    return (
      <button
        className="location-search-icon"
        onClick={handleIconClick}
        title="Search map by place or address"
        aria-label="Search map by place or address"
      >
        <GlobeSearchIcon size={18} strokeWidth={1.4} />
      </button>
    );
  }

  return (
    <div className="location-search-expanded" ref={containerRef}>
      <div className="search-container">
        <div className="search-icon"><SearchIcon size={14} /></div>
        <input
          ref={inputRef}
          id="location-search"
          name="location-search"
          placeholder="Find a place or address…"
          value={query}
          onChange={handleInputChange}
          className="location-search-input"
          autoComplete="off"
          aria-label="Search map by place or address"
          role="combobox"
          aria-expanded={suggestions.length > 0}
          aria-controls="location-search-suggestions"
        />
        {isLoading && (
          <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>
            ⏳
          </div>
        )}
        {query.trim() && (suggestions.length > 0 || isLoading || lastResultQuery === query.trim()) && (
          <div id="location-search-suggestions" className="search-suggestions">
            {isLoading ? (
              <div className="search-suggestion" style={{ color: '#666' }}>Searching...</div>
            ) : searchError ? (
              <div className="search-suggestion" style={{ color: '#b91c1c' }}>{searchError}</div>
            ) : suggestions.length === 0 && lastResultQuery === query.trim() ? (
              <div className="search-suggestion" style={{ color: '#666' }}>No results</div>
            ) : (
              suggestions.map((r, i) => {
                const parts = r.display_name.split(', ');
                const primary = parts[0] || r.display_name;
                const detail = parts.slice(1, 4).join(', ');
                return (
                  <button
                    key={i}
                    onClick={() => chooseResult(r)}
                    className="search-suggestion"
                    title={r.display_name}
                  >
                    <div className="search-suggestion-name">{primary}</div>
                    {detail && (
                      <div className="search-suggestion-detail">{detail}</div>
                    )}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationSearch;
